import React, { FC, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
    Button,
    Card,
    Collapse,
    FormControl,
    InputGroup
} from "react-bootstrap";
import { UPDATE_ORGANIZATION_CONFIG } from "../../../graphql/mutations";
import {
    Organization,
    OrganizationConfigData,
    OrganizationConfigVariables,
    SoftLimitsData,
    UpdateOrganizationConfigData
} from "../../../models/types";
import { GET_ORGANIZATION_CONFIG } from "../../../graphql/queries";
import { EditableJsonView } from "../../editable/EditableJsonView";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

interface Props {
    organization: Organization;
}

export const OrganizationConfig: FC<Props> = ({ organization }) => {
    const [show, toggleShow] = useState(false);
    const [updating, updateUpdating] = useState(false);
    const { data: { organizationConfig } = {} } =
        useQuery<OrganizationConfigData>(GET_ORGANIZATION_CONFIG, {
            variables: {
                organizationId: organization.id
            }
        });
    const [organizationConfigMutation] = useMutation<
        UpdateOrganizationConfigData,
        OrganizationConfigVariables
    >(UPDATE_ORGANIZATION_CONFIG);

    const createSoftLimits = () => {
        updateSoftLimits({ global: [] });
    };

    const updateSoftLimits = async (newLimits: SoftLimitsData | null) => {
        updateUpdating(true);
        try {
            await organizationConfigMutation({
                variables: {
                    input: {
                        organizationId: organization.id,
                        softLimits: newLimits
                    }
                },
                refetchQueries: [
                    {
                        query: GET_ORGANIZATION_CONFIG,
                        variables: { organizationId: organization.id }
                    }
                ]
            });
        } catch (error) {
            console.log("[DEBUG] error ", error);
        }
        updateUpdating(false);
    };

    const updateCanEditSchema = async (cantEditSchema: boolean) => {
        await organizationConfigMutation({
            variables: {
                input: {
                    organizationId: organization.id,
                    cantEditSchema: cantEditSchema
                }
            },
            refetchQueries: [
                {
                    query: GET_ORGANIZATION_CONFIG,
                    variables: { organizationId: organization.id }
                }
            ]
        });
    };

    const updateAnalyticsChance = async (analyticsChance: number | null) => {
        let newChance = analyticsChance;
        if (newChance !== null) {
            const clamped = Math.max(0, Math.min(100, Number(newChance)));
            newChance =
                Math.round((clamped / 100 + Number.EPSILON) * 10000) / 10000;
        }
        await organizationConfigMutation({
            variables: {
                input: {
                    organizationId: organization.id,
                    useAnalyticsChance: newChance
                }
            },
            refetchQueries: [
                {
                    query: GET_ORGANIZATION_CONFIG,
                    variables: { organizationId: organization.id }
                }
            ]
        });
    };

    return (
        <Card className="inner-card">
            <Card.Header className="text-inverse">
                Organization Config
                <FontAwesomeIcon
                    size="xs"
                    icon={show ? faChevronUp : faChevronDown}
                    className="mouseHover mx-2"
                    onClick={() => {
                        toggleShow(!show);
                    }}
                    title={show ? "Hide details" : "Show details"}
                />
            </Card.Header>
            <Collapse in={show}>
                <div>
                    <Card.Body>
                        {!organizationConfig?.softLimits ? (
                            <Button onClick={createSoftLimits}>
                                Create Soft Limits
                            </Button>
                        ) : (
                            <EditableJsonView
                                jsonData={JSON.stringify(
                                    organizationConfig.softLimits,
                                    null,
                                    2
                                )}
                                updateJsonData={updateSoftLimits}
                                updating={updating}
                                removeJsonData={() => updateSoftLimits(null)}
                            />
                        )}
                        <InputGroup className=" justify-content-center mt-2">
                            <InputGroup.Text>
                                Disable Analytics Schema Editing
                            </InputGroup.Text>
                            <InputGroup.Checkbox
                                title="Disable analytics schema editing"
                                aria-label="Disable analyticsschema editing "
                                checked={
                                    organizationConfig?.cantEditSchema || false
                                }
                                onChange={(event: any) => {
                                    updateCanEditSchema(event.target.checked);
                                }}
                            />
                        </InputGroup>
                        {organizationConfig?.useAnalyticsChance !== undefined &&
                        organizationConfig?.useAnalyticsChance !== null &&
                        organizationConfig?.useAnalyticsChance >= 0 ? (
                            <>
                                <InputGroup className="m-2">
                                    <InputGroup.Text>
                                        Analytics Chance (%)
                                    </InputGroup.Text>
                                    <FormControl
                                        type="number"
                                        min={0}
                                        max={100}
                                        step={0.01}
                                        className="analyticsChanceInput"
                                        defaultValue={
                                            organizationConfig?.useAnalyticsChance *
                                            100
                                        }
                                        onBlur={(event: any) => {
                                            const raw =
                                                event.target.value.trim();
                                            const value = Number(raw);
                                            const hasMax2Decimals =
                                                /^\d+(\.\d{1,2})?$/.test(
                                                    String(raw)
                                                );
                                            if (
                                                raw !== "" &&
                                                !isNaN(value) &&
                                                value >= 0 &&
                                                value <= 100 &&
                                                hasMax2Decimals
                                            ) {
                                                updateAnalyticsChance(value);
                                            }
                                        }}
                                    />
                                    <Button
                                        variant="danger"
                                        onClick={() => {
                                            updateAnalyticsChance(null);
                                        }}
                                    >
                                        Remove
                                    </Button>
                                </InputGroup>
                            </>
                        ) : (
                            <Button
                                className="m-2"
                                onClick={() => {
                                    updateAnalyticsChance(10);
                                }}
                            >
                                Set Analytics Chance
                            </Button>
                        )}
                    </Card.Body>
                </div>
            </Collapse>
        </Card>
    );
};
